import { PageIcon } from "@/components/PageIcon";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ScanBarcode, Loader2, Package, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useLocations, StockActionsBar } from "@/components/stock/StockActions";
import { LocationTypePill } from "@/components/stock/StockBadges";
import { formatApiErrorDetail } from "@/lib/api";

export default function StockScanPage() {
  const navigate = useNavigate();
  const [locations] = useLocations();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const scan = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    setBusy(true); setResult(null);
    try { const r = await api.get("/stock/scan", { params: { code: code.trim() } }); setResult(r.data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Not found"); }
    finally { setBusy(false); }
  };

  const p = result?.product;

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="stock-scan">
      <div className="flex items-center gap-3 mb-4"><PageIcon />
        <div><h1 className="text-2xl font-bold text-slate-900">Barcode / SKU Scan</h1><p className="text-sm text-slate-500">Scan or type a barcode, SKU or serial to find a product and act on its stock.</p></div>
      </div>

      <form onSubmit={scan} className="flex gap-2 mb-6">
        <Input autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="Scan or enter a code…" className="text-lg py-6" data-testid="scan-input" />
        <Button type="submit" disabled={busy} className="bg-teal-600 hover:bg-teal-700 text-white px-6" data-testid="scan-submit">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Scan"}</Button>
      </form>

      {p && (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6" data-testid="scan-result">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0"><Package className="w-5 h-5" /></span>
              <div>
                <h2 className="text-lg font-bold text-slate-900">{p.title}</h2>
                <div className="text-xs text-slate-400 font-mono">ID {p.legacy_id}{p.sku ? ` · SKU ${p.sku}` : ""}{p.barcode ? ` · ${p.barcode}` : ""}</div>
                <div className="text-sm text-slate-500 mt-1">Matched by <span className="font-medium">{result.matched_by}</span> · Available: <span className="font-semibold text-emerald-600">{p.available ?? "—"}</span></div>
              </div>
            </div>
            <Button variant="outline" className="gap-1.5 shrink-0" onClick={() => navigate(`/products/${p.legacy_id}`)} data-testid="scan-open-product">Open <ArrowRight className="w-4 h-4" /></Button>
          </div>

          {result.serial && (
            <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-800">Serial <span className="font-mono">{result.serial.serial_number}</span> — status: {result.serial.status}</div>
          )}

          {(result.locations || []).length > 0 && (
            <div className="mt-5">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Stock at locations</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {result.locations.map((l) => (
                  <div key={l.location_id} className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2">
                    <div><div className="text-sm font-medium text-slate-800">{l.location_name}</div><LocationTypePill type={l.location_type} /></div>
                    <div className="text-right"><div className="text-sm font-semibold text-emerald-600">{l.qty_available}</div><div className="text-[10px] text-slate-400">available</div></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5 pt-4 border-t border-slate-100">
            <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Quick actions</div>
            <StockActionsBar productId={p.legacy_id} locations={locations} serialized={p.serialized} onDone={() => scan()} />
          </div>
        </div>
      )}
    </div>
  );
}
